/**
 * Small pieces shared by every governance and admin screen. They live here rather than in
 * `@/components/ui` because L3 owns the global kit — this lane adds nothing global.
 *
 * Nothing here invents colour: state chips take `workflow_state.color`, band and tier chips take
 * `option.color`, and both fall back to the tenant's secondary surface token rather than a Tailwind blue.
 */
import type { ReactNode } from 'react';
import { AlertTriangle, Loader2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button, EmptyState as KitEmptyState, PageHead } from '@/kit';
import { Skeleton } from '@/components/ui/skeleton';
import { StatePill } from '@/components/ui/pill';
import { cn } from '@/lib/utils';
import { usePageEyebrow } from '@/app/shell/page-eyebrow';

// ───────────────────────────────────────────────────────────────────── page furniture

export interface PageHeaderProps {
  title: string;
  /** One line that says what the page is for — never filler. */
  description?: ReactNode;
  eyebrow?: ReactNode;
  actions?: ReactNode;
  className?: string;
}

export function PageHeader({ title, description, eyebrow: own, actions, className }: PageHeaderProps) {
  const shell = usePageEyebrow();
  const eyebrow = own ?? shell;
  // R-13: the kit page head — eyebrow, 26px title, one line in ink-2, actions at the right.
  return <PageHead eyebrow={eyebrow} title={title} description={description} actions={actions} />;
}

/** A page-level container (R-13): full width; the shell's content region owns the 20px gutter. */
export function PageBody({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cn('page-body w-full', className)}>{children}</div>;
}

// ───────────────────────────────────────────────────────────────────────── empty/error

export interface EmptyStateProps {
  title: string;
  /** What is missing and why — written for the person looking at it. */
  body: ReactNode;
  /** Empty states always offer the next real action, never a dead end. */
  action?: { label: string; onClick: () => void } | ReactNode;
  icon?: ReactNode;
  className?: string;
}

export function EmptyState({ title, body, action, className }: EmptyStateProps) {
  const isDescriptor = Boolean(action) && typeof action === 'object' && action !== null && 'label' in (action as object);
  // R-13: the kit empty state — what would appear here, and one next action.
  const next = action ? (
    isDescriptor ? (
      <Button variant="primary" onClick={(action as { onClick: () => void }).onClick}>
        {(action as { label: string }).label}
      </Button>
    ) : (
      (action as ReactNode)
    )
  ) : undefined;
  return (
    <div className={className}>
      <KitEmptyState title={title} body={body} action={next} />
    </div>
  );
}

/**
 * A failed query. The API's own message is shown verbatim — a 403 explains itself ("you raised
 * this request"), and a route that does not exist yet says so instead of pretending to have data.
 */
export function ErrorState({ error, onRetry, className }: { error: unknown; onRetry?: () => void; className?: string }) {
  const message = error instanceof Error ? error.message : String(error ?? 'Something went wrong');
  return (
    <div className={cn('rounded-md border border-bad/40 bg-bad-soft px-5 py-4', className)}>
      <div className="flex items-start gap-3">
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />
        <div className="min-w-0 flex-1">
          <p className="text-[length:var(--t-meta)] font-medium text-foreground">This did not load</p>
          <p className="mt-1 break-words text-[length:var(--t-meta)] text-muted-foreground">{message}</p>
        </div>
        {onRetry ? (
          <Button onClick={onRetry}>
            Try again
          </Button>
        ) : null}
      </div>
    </div>
  );
}

export function LoadingRows({ rows = 5, className }: { rows?: number; className?: string }) {
  return (
    <div className={cn('space-y-2', className)} aria-busy="true" aria-live="polite">
      {Array.from({ length: rows }).map((_, i) => (
        <Skeleton key={i} className="h-12 w-full rounded-lg" />
      ))}
    </div>
  );
}

export function Spinner({ className }: { className?: string }) {
  return <Loader2 className={cn('h-4 w-4 animate-spin motion-reduce:animate-none', className)} />;
}

// ───────────────────────────────────────────────────────────────────────────── chips

export interface ColorChipProps {
  label: string;
  color?: string | null;
  title?: string;
  className?: string;
}

/**
 * A pill for a table-supplied hex (option.color, workflow_state.color). Delegates to the shared
 * StatePill (#27 #38) so the label ink is always derived from the fill and clears AA in both modes.
 */
export function ColorChip({ label, color, title, className }: ColorChipProps) {
  return <StatePill label={label} color={color} title={title} className={className} />;
}

/** Workflow state chip — colour from `workflow_state.color`, never a per-screen switch. */
export function StateChip({
  state,
  className,
}: {
  state: { code: string; label: string; color?: string | null } | null | undefined;
  className?: string;
}) {
  if (!state) return <Badge variant="outline" className={className}>No state</Badge>;
  return <ColorChip label={state.label} color={state.color} title={`State: ${state.label}`} className={className} />;
}

/** A monospace entity key — BEE-CAP-0142, REQ-2026-0007. */
export function EntityKey({ value, className }: { value: string; className?: string }) {
  return <span className={cn('font-mono text-[length:var(--t-th)] text-muted-foreground', className)}>{value}</span>;
}

// ──────────────────────────────────────────────────────────────────────── form fields

export interface FieldProps {
  label: string;
  htmlFor?: string;
  /** attribute_def.help_text, or the rule the field enforces. Always says something real. */
  hint?: ReactNode;
  error?: string | null;
  required?: boolean;
  children: ReactNode;
  className?: string;
}

export function Field({ label, htmlFor, hint, error, required, children, className }: FieldProps) {
  return (
    <div className={cn('space-y-1.5', className)}>
      <label htmlFor={htmlFor} className="flex items-center gap-1 text-[length:var(--t-meta)] font-medium text-foreground">
        {label}
        {required ? (
          <span className="text-destructive" aria-hidden>
            *
          </span>
        ) : null}
      </label>
      {children}
      {error ? (
        <p className="text-[length:var(--t-th)] font-medium text-destructive">{error}</p>
      ) : hint ? (
        <p className="text-[length:var(--t-th)] text-muted-foreground">{hint}</p>
      ) : null}
    </div>
  );
}

/** A labelled read-only fact — used in request detail and decision summaries. */
export function Fact({ label, children, className }: { label: string; children: ReactNode; className?: string }) {
  return (
    <div className={cn('min-w-0', className)}>
      <dt className="text-[length:var(--t-th)] uppercase tracking-wide text-muted-foreground">{label}</dt>
      <dd className="mt-0.5 truncate text-[length:var(--t-meta)] text-foreground">{children}</dd>
    </div>
  );
}

/** Character budget on a length-capped field (justification is ≤ 4000 by DATA-MODEL §3). */
export function CharCount({ value, max }: { value: string; max: number }) {
  const over = value.length > max;
  return (
    <span className={cn('tabular-nums text-[length:var(--t-th)]', over ? 'font-medium text-destructive' : 'text-muted-foreground')}>
      {value.length.toLocaleString()} / {max.toLocaleString()}
    </span>
  );
}
